import React, { Component } from 'react'
import PropTypes from 'prop-types'
import style from './LocationTextField.less'
import Autosuggest from 'react-autosuggest'
import r2rServicesClient from 'common/utils/r2rServicesClient'
import debounce from 'lodash/debounce'
import classNames from 'classnames'
import { ThemePrimary, ThemeSecondary } from './LocationTextFieldThemes'
import { logGaNonInteractionEvent } from 'common/utils/analytics'

import LocationCity from 'styles/icons/location-city.svg'
import LocationAirport from 'styles/icons/location-airport.svg'
import LocationStation from 'styles/icons/location-station.svg'
import LocationCountry from 'styles/icons/location-country.svg'
import LocationPlace from 'styles/icons/location-place.svg'
import LocationMarker from 'styles/icons/location-marker.svg'

// These are the google place types that Jayride considers "accurate"
// http://doc.jayride.com/create-quote-request
const googlePlaceTypeRestrictionsList = [
  'establishment',
  'address',
  'floor',
  'point_of_interest',
  'post_box',
  'premise',
  'room',
  'street_address',
  'street_number',
  'subpremise',
  'transit_station',
]

class LocationTextField extends Component {
  constructor(props) {
    super(props)
    this.state = {
      text: props.text,
      suggestions: [],
      isLoading: false,
      itemJustSelected: false,
      lastValue: props.text ? props.text : '',
      lastResponse: null,
    }
    this.isFocused = false
    this.onSuggestionsFetchRequested = debounce(this.onSuggestionsFetchRequested, 200)
  }

  componentWillReceiveProps = (nextProps) => {
    if (nextProps.text != this.state.text && !this.isFocused) {
      const nextText = (nextProps.text) ? nextProps.text : ''
      const nextState = {
        lastValue: nextText,
      }
      nextState.text = nextText
      this.setState(nextState)
    }
  }

  onKeyPress = (e) => {
    switch (e.charCode) {
      case 13:
        this.selectFirst(this.state.lastResponse)
        this.inputDOM.input.blur()
      default:
    }
  }

  onChange = (event, { newValue }) => {
    this.setState({
      text: newValue,
      hint: '',
    })
  };

  onFocus = () => {
    this.isFocused = true
    this.setState({
      text: '',
    })
  }

  onBlur = (event, { highlightedSuggestion }) => {
    this.isFocused = false

    if (this.state.itemJustSelected) {
      this.setState({
        itemJustSelected: false,
      })
      return
    }

    if (!this.props.forceInput && !this.state.text) {
      this.setState({
        text: '',
      })
      this.setNewOutput('', '', '')
    } else if (this.state.text == '') {
      this.setState({
        text: this.state.lastValue,
      })
    } else if (!highlightedSuggestion && this.state.lastResponse) {
      this.selectFirst(this.state.lastResponse)
    } else if (highlightedSuggestion && !this.state.isLoading) {
      const { canonicalName, longName, shortName } = highlightedSuggestion
      this.setState({
        text: longName,
      })
      this.setNewOutput(canonicalName, longName, shortName)
    } else if (this.state.lastValue && !this.state.isLoading) {
      this.setState({
        text: this.state.lastValue,
      })
    }
  }

  onSuggestionsFetchRequested = ({ value, reason }) => {
    if (reason == 'input-focused') {
      if (!this.state.lastResponse) {
        this.loadSuggestions(value)
      }
    } else if (reason == 'input-changed') {
      if (this.state.lastValue !== value) {
        this.loadSuggestions(value)
      }
    }
  }

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
      hint: '',
    })
  }

  setNewOutput = (canonicalName, longName, shortName, googlePlaceId) => {
    this.setState({
      lastValue: longName,
    }, () => {
      this.props.onChange({ canonicalName, longName, shortName, googlePlaceId })
    })
  }

  getSuggestionValue = (suggestion) => {
    const { canonicalName, longName, shortName, googlePlaceId } = suggestion
    this.setState({
      text: suggestion.longName,
    }, () => {
      this.setNewOutput(canonicalName, longName, shortName, googlePlaceId)
    })
    return suggestion.longName
  }

  getClasses = () => {
    const classes = [style.main]
    if (this.props.className) {
      classes.push(this.props.className)
    }
    if (this.props.invalid) {
      classes.push(style.invalid)
    }
    return classNames(classes)
  }

  getHintStyle = () => {
    const classes = [style.hint]
    if (this.props.markerIcon) {
      classes.push(style.hasMarker)
    }
    return classNames(classes)
  }

  getTheme = () => (this.props.theme === 'secondary' ? ThemeSecondary : ThemePrimary)

  handleSuggestionSelected = () => {
    this.setState({
      itemJustSelected: true,
    })
  };

  componentDidCatch(error, info) {
    logGaNonInteractionEvent(
      'Tickets', 'API:LocationTextField', `Error:${error}, ${info}`
    )
  }

  loadSuggestions = (text) => {
    const { type, autocompleteEndpoint, radius, location, languageCode } = this.props
    this.currentValue = text

    this.setState({
      isLoading: true,
      hint: '',
    })

    const apiOptions = {
      params: {
        key: '86f5qBa1',
        query: text,
      },
      sendAnalyticsIds: false,
    }

    if (type) {
      apiOptions.params.type = type
    }

    if (languageCode) {
      apiOptions.params.languageCode = languageCode
    }

    if (radius) {
      apiOptions.params.radius = radius
    }

    if (location) {
      apiOptions.params.location = `${location.lat},${location.lng}`
    }

    r2rServicesClient.get(autocompleteEndpoint, apiOptions)
      .then((response) => {
        this.processSuggestions(response)
      })
  }

  processSuggestions = (response) => {
    if (this.isFocused) {
      this.showHints(response)
    } else {
      this.selectFirst(response)
    }
  }

  showHints = (response) => {
    // Will extend
    let suggestions = response.data.results.map(place => place)

    // Limits the suggestions to jayride compatible
    if (this.props.hasGooglePlaceTypeRestrictions) {
      suggestions = suggestions.filter(suggestion =>
        suggestion.kinds.some(kind =>
          googlePlaceTypeRestrictionsList.includes(kind)
        )
      )
    }

    // Create Shadow
    const hint = suggestions.length > 0 ? this.createHintText(suggestions[0].longName) : ''

    this.setState({
      isLoading: false,
      suggestions,
      hint,
      lastResponse: response,
    })
  }

  selectFirst = (reponse) => {
    const places = reponse.data.results
    if (places.length == 0) return
    const target = places[0]
    const outputArguments = [
      target.canonicalName, target.longName, target.shortName,
    ]
    if (target.googlePlaceId) {
      outputArguments.push(target.googlePlaceId)
    }
    this.setNewOutput(...outputArguments)
  }

  createHintText = (suggestionText) => {
    let returnString = ''
    const lowercaseSuggestion = suggestionText.toLowerCase()
    const lowercaseQuery = this.currentValue.toLowerCase()
    if (lowercaseSuggestion.indexOf(lowercaseQuery) == 0) {
      returnString = (
        <span className={style.wrapper}>
          <span className={style.hintPre}>
            {this.currentValue}
          </span>
          <span className={style.hintPost}>
            {suggestionText.slice(this.currentValue.length)}
          </span>
        </span>
      )
    }
    return returnString
  }

  renderSuggestion = (suggestion) => {
    let iconSt = ''

    switch (suggestion.kind) {
      case 'city':
      case 'capital':
      case 'town':
      case 'village':
        iconSt = LocationCity
        break
      case 'airport':
        iconSt = LocationAirport
        break
      case 'station':
        iconSt = LocationStation
        break
      case 'country':
        iconSt = LocationCountry
        break
      default:
        iconSt = LocationPlace
        break
    }

    let icon = (
      <svg viewBox={iconSt.viewBox}>
        <use xlinkHref={`#${iconSt.id}`} />
      </svg>
    )

    return (
      <span className={style.suggestionContent}>
        <span className={style.suggestionIcon}>{icon}</span>
        <span className={style.suggestionText}>{suggestion.longName}</span>
      </span>
    )
  }

  render() {
    const { text, suggestions, hint } = this.state
    const { markerIcon } = this.props

    const inputProps = {
      value: text,
      onChange: this.onChange,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      onKeyPress: this.onKeyPress,
      placeholder: this.props.placeholder,
    }
    return (
      <div
        className={this.getClasses()}
        data-test="locationTextField"
      >
        {markerIcon &&
          <svg className={style.markerIcon} viewBox={LocationMarker.viewBox}>
            <use xlinkHref={`#${LocationMarker.id}`} />
          </svg>
        }
        <span className={this.getHintStyle()}>{hint}</span>
        <Autosuggest
          theme={this.getTheme()}
          onSuggestionSelected={this.handleSuggestionSelected}
          alwaysRenderSuggestions={false}
          suggestions={suggestions}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          getSuggestionValue={this.getSuggestionValue}
          renderSuggestion={this.renderSuggestion}
          inputProps={inputProps}
          ref={(inputDOM) => { this.inputDOM = inputDOM }}
        />
        {this.props.invalid &&
          <span className={style.formError}>
            {this.props.invalid}
          </span>
        }
      </div>
    )
  }
}

LocationTextField.defaultProps = {
  forceInput: true,
}

LocationTextField.propTypes = {
  text: PropTypes.string,
  onChange: PropTypes.func,
  invalid: PropTypes.oneOfType([PropTypes.element, PropTypes.string]), // TODO: Get rid of string?
  className: PropTypes.string,
  theme: PropTypes.string,
  forceInput: PropTypes.bool, // Forces a value onBlur
  markerIcon: PropTypes.bool,
  autocompleteEndpoint: PropTypes.string.isRequired,
  radius: PropTypes.number,
  location: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  type: PropTypes.string, // can by r2r or google, null is both
  hasGooglePlaceTypeRestrictions: PropTypes.bool,
  placeholder: PropTypes.string,
  languageCode: PropTypes.string,
}

export default LocationTextField
